import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  MdCheckCircleOutline,
  MdOutlineTaskAlt,
  MdDoneAll,
  MdOutlineWhatsapp,
  MdPlayArrow,
} from "react-icons/md";
import axios from "../config/axiosInstance";
import { CRM } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import {
  Badge,
  Card,
  EmptyState,
  ErrorBlock,
} from "../components/crm/Primitives";
import {
  SkeletonLine,
  SkeletonWorkList,
} from "../components/crm/Skeletons";
import WorkRow from "../components/crm/WorkRow";
import AccountDrawer from "../components/crm/AccountDrawer";
import QueueMix from "../components/crm/QueueMix";
import DayProgress from "../components/crm/DayProgress";
import {
  formatMoney,
  formatNumber,
  formatRelativeDays,
  whatsappLink,
} from "../components/crm/crmHelpers";

/*
  "Hoje" — a tela inicial de quem atende.

  A primeira versão desta tela eram duas colunas lado a lado: tarefas à
  esquerda, oito cartões de fila à direita. Três problemas que só apareceram
  com a base cheia:

  1. **Desequilíbrio.** Uma tarefa aberta deixava a coluna esquerda vazia por
     800px enquanto a direita rolava por oito cartões.
  2. **Parágrafo repetido.** Cada cartão trazia a explicação da regra. Oito
     explicações de três linhas, lidas de novo todo dia, para informação que
     se aprende uma vez.
  3. **Não dizia o que fazer primeiro.** Oito cartões de peso igual, ordenados
     por receita, punham "sem responsável" (60 contas, arrumação de carteira)
     acima de "renovação em risco" (4 contas, receita saindo pela porta).

  Agora é UMA lista ordenada por urgência — as tarefas atrasadas no topo,
  depois as contas das filas na ordem de ataque que o servidor define
  (`priority`). As filas viram um filtro em chips, e a explicação da regra
  aparece só da fila selecionada. Clicar numa linha abre o painel lateral: a
  conta é trabalhada sem perder o lugar na lista.
*/

// A lista é de trabalho, não um relatório: além disto, vire a página.
const FEED_LIMIT = 40;

const TodayPage = () => {
  const queryClient = useQueryClient();
  const { user } = useApplication();
  const [selectedQueue, setSelectedQueue] = useState("all");
  const [openId, setOpenId] = useState(null);

  const tasksQuery = useQuery(["crm", "tasks"], async () => {
    const { data } = await axios.get(CRM.TASKS);
    return data.data;
  });

  const queuesQuery = useQuery(["crm", "queues"], async () => {
    const { data } = await axios.get(CRM.QUEUES);
    return data.data;
  });

  const completeTask = useMutation(
    (taskId) => axios.put(`${CRM.TASK}/${taskId}`, { status: "DONE" }),
    {
      onSettled: () => {
        queryClient.invalidateQueries(["crm", "tasks"]);
        queryClient.invalidateQueries(["crm", "queues"]);
      },
    }
  );

  const queues = useMemo(
    () => queuesQuery.data?.queues || [],
    [queuesQuery.data]
  );

  /*
    As filas chegam ordenadas por prioridade do servidor. A lista achata todas
    nessa ordem e remove repetição: uma conta pode satisfazer duas regras
    (vencendo E parada), e deve aparecer uma vez só, na fila mais urgente — é
    lá que está a ação certa.
  */
  const feed = useMemo(() => {
    const visible =
      selectedQueue === "all"
        ? queues.filter((queue) => queue.id !== "unassigned")
        : queues.filter((queue) => queue.id === selectedQueue);

    const seen = new Set();
    const rows = [];

    visible.forEach((queue) => {
      queue.rows.forEach((row) => {
        if (seen.has(row.id)) return;
        seen.add(row.id);
        rows.push({ row, queue });
      });
    });

    return rows;
  }, [queues, selectedQueue]);

  const visibleFeed = feed.slice(0, FEED_LIMIT);

  const openIndex = visibleFeed.findIndex((item) => item.row.id === openId);
  const goTo = (index) => {
    const next = visibleFeed[index];
    if (next) setOpenId(next.row.id);
    else setOpenId(null);
  };

  const tasks = tasksQuery.data;
  const overdue = tasks?.overdue || [];
  const dueToday = tasks?.today || [];
  const contactable = queues.filter((queue) => queue.id !== "unassigned");
  // Filas que de fato têm trabalho hoje — "zeradas" continuam na mistura só
  // como ausência, não como item da contagem.
  const activeQueues = contactable.filter((queue) => queue.count > 0);
  const totalToContact = contactable.reduce((sum, q) => sum + q.count, 0);
  const valueAtStake = contactable.reduce((sum, q) => sum + q.valueAtStake, 0);
  const unassigned = queues.find((queue) => queue.id === "unassigned");

  const activeQueue =
    selectedQueue === "all"
      ? null
      : queues.find((queue) => queue.id === selectedQueue);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-bold text-slate-900">
          {user?.name ? `Bom trabalho, ${user.name.split(" ")[0]}` : "Hoje"}
        </h1>
        <p className="text-sm text-slate-500">
          Em ordem de urgência. Clique numa conta para trabalhá-la sem sair da
          lista.
        </p>
      </header>

      {/*
        Três blocos de leitura rápida, nesta ordem: o que VOCÊ já fez (o único
        número que sobe com o trabalho), o tamanho do que falta, e a receita
        exposta. A linha de três números que havia aqui antes não dava escala
        a nada — "40 contas" podia ser o dia inteiro ou um décimo dele.
      */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <DayProgress today={queuesQuery.data?.today} />

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            A contatar
          </p>
          <p className="mt-1 flex items-baseline gap-1.5">
            <span className="text-3xl font-bold leading-none text-slate-900">
              {formatNumber(totalToContact)}
            </span>
            <span className="text-sm text-slate-500">contas</span>
          </p>
          <p className="mt-2 text-[11px] text-slate-500">
            em {activeQueues.length} fila(s) ·{" "}
            {formatNumber(
              contactable.reduce((sum, q) => sum + q.snoozed, 0)
            )}{" "}
            aguardando retorno
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Receita em jogo
          </p>
          <p className="mt-1 flex items-baseline gap-1.5">
            <span
              className={`text-3xl font-bold leading-none ${
                valueAtStake ? "text-amber-600" : "text-slate-400"
              }`}
            >
              {formatMoney(valueAtStake)}
            </span>
          </p>
          <p className="mt-2 text-[11px] text-slate-500">
            mensalidade das contas que esperam contato
          </p>
        </div>
      </div>

      {overdue.length || dueToday.length ? (
        <Card className="overflow-hidden border-rose-200">
          <div className="flex items-center gap-2 border-b border-rose-100 bg-rose-50/70 px-4 py-2">
            <MdOutlineTaskAlt className="text-rose-600" size={16} />
            <p className="text-xs font-bold uppercase tracking-wider text-rose-800">
              Compromissos seus
            </p>
            <Link
              to="/tarefas"
              className="ml-auto text-xs font-semibold text-rose-700 hover:underline"
            >
              ver todas
            </Link>
          </div>

          {[...overdue, ...dueToday].map((task) => {
            const phone = whatsappLink(task.company?.phoneNumber);

            return (
              <div
                key={task.id}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-2.5 last:border-0"
              >
                <button
                  type="button"
                  onClick={() => completeTask.mutate(task.id)}
                  title="Marcar como feita"
                  className="shrink-0 text-slate-300 transition hover:text-emerald-500"
                >
                  <MdCheckCircleOutline size={20} />
                </button>

                <button
                  type="button"
                  onClick={() => setOpenId(task.companyId)}
                  className="min-w-0 flex-1 text-left"
                >
                  <p className="truncate text-sm font-semibold text-slate-800">
                    {task.title}
                  </p>
                  <p className="truncate text-[11px] text-slate-500">
                    {task.company?.name || `Cliente #${task.companyId}`}
                    {task.dueDate
                      ? ` · venceu ${formatRelativeDays(task.dueDate)}`
                      : ""}
                  </p>
                </button>

                {task.isOverdue ? <Badge tone="danger">atrasada</Badge> : null}
                {phone ? (
                  <a
                    href={phone}
                    target="_blank"
                    rel="noreferrer"
                    title="Abrir WhatsApp"
                    className="rounded-lg p-1.5 text-slate-300 transition hover:bg-emerald-50 hover:text-emerald-600"
                  >
                    <MdOutlineWhatsapp size={18} />
                  </a>
                ) : null}
              </div>
            );
          })}
        </Card>
      ) : null}

      {queuesQuery.isLoading ? (
        <>
          <SkeletonLine className="h-24 w-full rounded-2xl" />
          <SkeletonWorkList rows={6} />
        </>
      ) : queuesQuery.error ? (
        <ErrorBlock
          message="Não foi possível carregar as filas."
          onRetry={queuesQuery.refetch}
        />
      ) : (
        <>
          {/*
            A mistura das filas como barra empilhada, clicável: além de mostrar
            onde o trabalho está concentrado, é o controle de filtro da lista
            abaixo. Os chips que havia aqui diziam o mesmo em texto, sem dar
            noção de proporção — oito filas de tamanhos muito diferentes
            pareciam oito botões iguais.
          */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                Onde está o trabalho
              </p>
              {selectedQueue !== "all" ? (
                <button
                  type="button"
                  onClick={() => setSelectedQueue("all")}
                  className="text-[11px] font-semibold text-pink-700 hover:underline"
                >
                  limpar filtro
                </button>
              ) : null}
            </div>

            <QueueMix
              queues={contactable}
              selected={selectedQueue}
              onSelect={setSelectedQueue}
              total={totalToContact}
            />
          </div>

          {activeQueue ? (
            <div className="rounded-xl bg-slate-50 px-4 py-3">
              <p className="text-xs text-slate-600">{activeQueue.why}</p>
              <p className="mt-0.5 text-xs font-semibold text-slate-800">
                {activeQueue.action}
                {activeQueue.snoozed > 0 ? (
                  <span className="ml-2 font-normal text-slate-500">
                    · {activeQueue.snoozed} aguardando retorno
                  </span>
                ) : null}
              </p>
            </div>
          ) : null}

          <Card className="overflow-hidden">
            {visibleFeed.length === 0 ? (
              <EmptyState
                icon={MdDoneAll}
                title="Nada para contatar agora"
                description="Nenhuma conta se encaixa nas regras hoje — ou todas já receberam contato recente. Isso é trabalho feito, não falta de dados."
              />
            ) : (
              <>
                <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-4 py-2">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Contas a contatar
                  </p>
                  <button
                    type="button"
                    onClick={() => setOpenId(visibleFeed[0].row.id)}
                    className="ml-auto inline-flex items-center gap-1 rounded-lg bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-slate-700"
                  >
                    <MdPlayArrow size={14} /> trabalhar a lista
                  </button>
                </div>

                {visibleFeed.map(({ row, queue }) => (
                  <WorkRow
                    key={row.id}
                    row={row}
                    highlight={queue.highlight}
                    queueId={queue.id}
                    queueLabel={selectedQueue === "all" ? queue.label : null}
                    isActive={openId === row.id}
                    onOpen={setOpenId}
                  />
                ))}

                {feed.length > FEED_LIMIT ? (
                  <p className="px-4 py-3 text-xs text-slate-500">
                    Mostrando {FEED_LIMIT} de {formatNumber(feed.length)}. A
                    lista recalcula sozinha conforme você registra os contatos.
                  </p>
                ) : null}
              </>
            )}
          </Card>

          {unassigned && unassigned.count > 0 ? (
            <Link
              to="/filas/unassigned"
              className="flex items-center gap-3 rounded-xl border border-dashed border-slate-300 px-4 py-3 transition hover:border-pink-300"
            >
              <Badge tone="neutral">{formatNumber(unassigned.count)}</Badge>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-slate-700">
                  Contas sem responsável
                </span>
                <span className="block text-xs text-slate-500">
                  Arrumação de carteira — não aparece na lista de contato
                  porque não é trabalho do dia.
                </span>
              </span>
            </Link>
          ) : null}
        </>
      )}

      {openId ? (
        <AccountDrawer
          companyId={openId}
          onClose={() => setOpenId(null)}
          position={openIndex >= 0 ? openIndex + 1 : null}
          total={visibleFeed.length}
          onPrev={openIndex > 0 ? () => goTo(openIndex - 1) : undefined}
          onNext={
            openIndex >= 0 && openIndex < visibleFeed.length - 1
              ? () => goTo(openIndex + 1)
              : undefined
          }
        />
      ) : null}
    </div>
  );
};

export default TodayPage;
